import type { PerfRow } from "../types";

import {
  CreativePreview,
  type MarketingBannerType,
} from "../../MarketingBanners";

/** Thumbnail box dimensions (matches the h-10 w-12 placeholder: 48×40px). */
const THUMB_WIDTH = 48;
const THUMB_HEIGHT = 40;

const RENDERABLE_TYPES: readonly string[] = ["html_ad", "image_banner"];

/**
 * Renders a thumbnail for a banner.
 *
 * When the row carries the banner's `creativeCode` (and a renderable type) we
 * reuse the marketing-banner-library `CreativePreview` so the Source tab shows
 * the actual banner (image or scaled HTML ad). Otherwise we fall back to an
 * explicit `thumb` image, and finally to a dimension-shaped placeholder.
 *
 * @param param0 - The props object containing the banner row.
 * @returns A JSX element representing the banner thumbnail.
 */
export const BannerThumb = ({ row }: { row: PerfRow }) => {
  const [bw, bh] = (row.dim ?? "1×1")
    .split(/[×x]/)
    .map((n) => parseFloat(n) || 1);

  if (row.creativeCode && row.tag && RENDERABLE_TYPES.includes(row.tag)) {
    return (
      <CreativePreview
        banner={{
          id: "",
          name: row.name,
          creative_code: row.creativeCode,
          type: row.tag as MarketingBannerType,
          offer_id: "",
          creative_file_id: "",
          creative_file_name: "",
          banner_group: row.group ?? "",
          width: bw,
          height: bh,
          language: row.lang ?? "",
          hoid: row.hoid ?? "",
          created_at: "",
        }}
        maxWidth={THUMB_WIDTH}
        maxHeight={THUMB_HEIGHT}
      />
    );
  }

  if (row.thumb) {
    return (
      <div className="flex h-10 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-surface-secondary">
        <img
          src={row.thumb}
          alt=""
          aria-hidden
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  const scale = Math.min(40 / bw, 32 / bh);
  return (
    <div className="flex h-10 w-12 shrink-0 items-center justify-center rounded-lg bg-surface-secondary p-1">
      <div
        className="rounded-sm bg-surface-tonal-bolder"
        style={{
          width: Math.max(3, Math.round(bw * scale)),
          height: Math.max(3, Math.round(bh * scale)),
        }}
      />
    </div>
  );
};
